import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowDown, Check, Mail } from "lucide-react";
import { cn } from "@/lib/utils";
import { LightMarkdown } from "@/components/common";
import { FileRow } from "@/components/intake/FileRow";
import { FindingsCard } from "@/components/intake/FindingsCard";

const LABELS = { uploading: ["Laster opp og leser dokumentene…", "Setter roller på vedleggene…"], analyzing: ["Finner kunde, fartøy og frister…", "Leser arbeidspostene…", "Ser etter arbeid som finnes fra før…"], chatting: ["Leser i dokumentene…"], creating: ["Oppretter arbeidet…", "Flytter dokumentene og samtalen over…"] };

function Thinking({ phase }) {
  const list = LABELS[phase] || LABELS.chatting;
  const [i, setI] = useState(0);
  useEffect(() => { const t = setInterval(() => setI((v) => (v + 1) % list.length), 2200); return () => clearInterval(t); }, [list.length]);
  return <div className="msg-enter flex items-center gap-3 text-[14px] text-muted-foreground" data-testid="intake-thinking"><span className="pulse-dot h-3 w-3 rounded-full bg-[hsl(var(--brand))]" /><span key={i} className="fade-in">{list[i]}</span></div>;
}

/** Handed over from Innboks: the e-mail itself sits first in the conversation, folded – the subject and sender in one line. */
function SourceCard({ sr, text }) {
  const [open, setOpen] = useState(false);
  const body = (text || "").replace(/^(Emne|Fra|Mottatt):.*\n?/gm, "").trim();
  return (
    <div className="msg-enter flex justify-end" data-testid="intake-source">
      <div className="w-full max-w-[560px] rounded-[18px] bg-[hsl(210_20%_94.5%)] px-4 py-3">
        <div className="flex items-start gap-2.5">
          <Mail className="mt-[3px] h-4 w-4 shrink-0 text-foreground/50" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-[14.5px] font-medium text-foreground" data-testid="intake-source-subject">{sr.subject}</div>
            <div className="truncate text-[12.5px] text-muted-foreground">{[sr.customer, sr.number ? `#${sr.number}` : null, sr.source === "web_form" ? "nettskjema" : "e-post"].filter(Boolean).join(" · ")}</div>
          </div>
          {body ? <button type="button" onClick={() => setOpen((v) => !v)} className="shrink-0 text-[12.5px] font-medium text-foreground/60 hover:text-foreground" data-testid="intake-source-toggle">{open ? "Skjul" : "Vis e-posten"}</button> : null}
        </div>
        {open ? <div className="mt-3 whitespace-pre-wrap border-t border-black/[0.06] pt-3 text-[14px] leading-[1.6] text-foreground/85" data-testid="intake-source-body">{body}</div> : null}
      </div>
    </div>
  );
}

/** Files travel as one «message» from you – with live progress and the server's verdict on each. */
function FilesBubble({ rows, intake }) {
  const n = rows.length;
  return (
    <div className="msg-enter flex flex-col items-end gap-1.5" data-testid="intake-files-bubble">
      <div className="w-full max-w-[560px] rounded-[18px] bg-[hsl(210_20%_94.5%)] px-3 py-2.5">
        <div className="px-1 pb-1.5 text-[13px] text-foreground/70">{n} {n === 1 ? "fil" : "filer"}</div>
        <ul className="space-y-1.5" data-testid="intake-files">{rows.map((r) => <FileRow key={r.key} row={r} onRetry={intake.retry} onRemove={intake.remove} compact />)}</ul>
      </div>
    </div>
  );
}

function Assistant({ m, last, onQuick, children }) {
  const done = (m.actions_done || []).filter((a) => a.label);
  return (
    <div className="msg-enter min-w-0" data-testid="intake-message-assistant" data-message-id={m.id}>
      {done.length ? <ul className="mb-2.5 space-y-1" data-testid="intake-actions-done">{done.map((a, i) => <li key={i} className="flex items-start gap-2 text-[12.5px] leading-[1.5] text-muted-foreground"><Check className="mt-[3px] h-3.5 w-3.5 shrink-0 text-primary" strokeWidth={2.6} />{a.label}</li>)}</ul> : null}
      <LightMarkdown text={m.content} className="text-[15px] leading-[1.65] text-foreground/90 [overflow-wrap:anywhere] [&_strong]:font-medium [&_strong]:text-foreground [&_ul]:mt-1.5 [&_ul]:space-y-0.5 [&_li]:leading-[1.55] [&_p+p]:mt-2.5 [&_em]:not-italic [&_em]:text-muted-foreground" />
      {children}
      {last && m.quick_replies?.length ? <div className="mt-3 flex flex-wrap gap-1.5" data-testid="intake-quick-replies">{m.quick_replies.slice(0, 3).map((q, i) => <button key={i} type="button" onClick={() => onQuick(q)} className={cn("h-8 rounded-full border px-3 text-[12.5px] font-medium transition-[border-color,color,background-color]", q === "Opprett arbeid" ? "border-[hsl(var(--brand))] bg-[hsl(var(--brand))] text-white hover:bg-[hsl(var(--brand-strong))]" : "border-black/[0.08] bg-white text-foreground/75 hover:border-black/[0.18] hover:text-foreground")} data-testid="intake-quick-reply">{q}</button>)}</div> : null}
    </div>
  );
}

/**
 * The conversation before work exists, read top to bottom: your files and words on the right, the assistant on the left,
 * and – after the reading – one card with what it found and the single action «Opprett arbeid».
 */
export function IntakeChat({ intake, onOpenDoc, onCreate, creating }) {
  const { session, documents, rows, phase, busy } = intake;
  const listRef = useRef(null);
  const [atBottom, setAtBottom] = useState(true);
  const messages = useMemo(() => session?.messages || [], [session]);
  const timeline = useMemo(() => {
    const ev = [...rows.map((r) => ({ kind: "file", at: r.at || "", r })), ...messages.map((m) => ({ kind: "msg", at: m.created_at, m }))].sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0));
    const out = [];
    ev.forEach((e) => { if (e.kind === "file" && out.length && out[out.length - 1].kind === "files") out[out.length - 1].rows.push(e.r); else if (e.kind === "file") out.push({ kind: "files", rows: [e.r], key: `files-${e.r.key}` }); else out.push({ ...e, key: e.m.id }); });
    return out;
  }, [rows, messages]);
  const cardAfter = useMemo(() => [...messages].reverse().find((m) => m.findings)?.id || null, [messages]);
  const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant")?.id;
  const locked = session?.status === "created";

  useEffect(() => { const el = listRef.current; if (el && atBottom) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" }); }, [timeline.length, busy, session?.findings, atBottom]);
  const onScroll = () => { const el = listRef.current; if (el) setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 64); };

  return (
    <div className="relative min-h-0 flex-1">
      <div ref={listRef} onScroll={onScroll} className="scroll-fade-y h-full overflow-y-auto px-5 pb-8 pt-6 scrollbar-thin sm:px-8" data-testid="intake-scroll">
        <div className="mx-auto flex max-w-[760px] flex-col gap-6">
          {session?.source_request ? <SourceCard sr={session.source_request} text={session.text} /> : null}
          {timeline.map((e) => e.kind === "files" ? <FilesBubble key={e.key} rows={e.rows} intake={intake} />
            : e.m.role === "user" ? <div key={e.key} className="msg-enter flex justify-end" data-testid="intake-message-user"><div className="max-w-[85%] whitespace-pre-wrap rounded-[18px] bg-[hsl(210_20%_94.5%)] px-4 py-2.5 text-[15px] leading-[1.6] text-foreground">{e.m.content}</div></div>
              : (
                <Assistant key={e.key} m={e.m} last={e.m.id === lastAssistant && !busy} onQuick={(q) => (q === "Opprett arbeid" ? onCreate() : intake.send(q))}>
                  {e.m.id === cardAfter && session?.findings ? <FindingsCard f={session.findings} documents={documents} onOverride={intake.setOverrides} onOpenDoc={onOpenDoc} onCreate={onCreate} creating={creating} locked={locked} /> : null}
                </Assistant>
              ))}
          {busy ? <Thinking phase={phase} /> : null}
        </div>
      </div>
      {!atBottom ? <button type="button" onClick={() => { listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" }); setAtBottom(true); }} className="absolute bottom-4 right-6 z-10 flex h-8 w-8 items-center justify-center rounded-full border border-black/[0.08] bg-white text-foreground/70 shadow-md-soft hover:text-foreground sm:right-10" aria-label="Til bunnen" data-testid="intake-scroll-bottom"><ArrowDown className="h-4 w-4" /></button> : null}
    </div>
  );
}
